import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../layouts/DashboardLayout';
import { usersApi, rolesApi, departmentsApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, PencilSimple, Trash, MagnifyingGlass, CircleNotch, UserCircle, Eye, EyeSlash } from '@phosphor-icons/react';
import { formatDate, formatApiErrorDetail } from '../lib/utils';
import PageHeader from '../components/layout/PageHeader';
import { toast } from 'sonner';
import {
  PASSWORD_POLICY_HINT,
  passwordErrorMessage,
  validatePasswordParts,
} from '../lib/passwordValidation';

export default function UsersPage() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role_id: '',
    department_id: '',
  });

  const loadData = useCallback(async () => {
    try {
      const [usersRes, rolesRes, deptsRes] = await Promise.all([
        usersApi.list(),
        rolesApi.list(),
        departmentsApi.list(),
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setDepartments(deptsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenDialog = (user = null) => {
    setShowPassword(false);
    if (user) {
      setEditingUser(user);
      const roleExists = roles.some((r) => r.id === user.role_id);
      const deptExists = departments.some((d) => d.id === user.department_id);
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '',
        role_id: user.role_id && roleExists ? user.role_id : 'none',
        department_id: user.department_id && deptExists ? user.department_id : 'none',
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role_id: 'none',
        department_id: 'none',
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingUser) {
        const updateData = { name: formData.name };
        if (formData.role_id && formData.role_id !== 'none') updateData.role_id = formData.role_id;
        if (formData.department_id && formData.department_id !== 'none') updateData.department_id = formData.department_id;
        await usersApi.update(editingUser.id, updateData);
        toast.success('User updated successfully');
      } else {
        const passwordCheck = validatePasswordParts(formData.password);
        if (!passwordCheck.valid) {
          toast.error(passwordErrorMessage(passwordCheck, t));
          setSaving(false);
          return;
        }
        const createData = { ...formData, password: passwordCheck.password };
        if (createData.role_id === 'none') delete createData.role_id;
        if (createData.department_id === 'none') delete createData.department_id;
        await usersApi.create(createData);
        toast.success('User created successfully');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await usersApi.delete(id);
      toast.success('User deleted successfully');
      loadData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const getRoleName = (roleId) => roles.find((r) => r.id === roleId)?.name || '-';
  const getDeptName = (deptId) => departments.find((d) => d.id === deptId)?.name || '-';

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="users-page">
        <PageHeader
          title={t('users.title')}
          description={t('users.subtitle')}
          actions={(
            <Button onClick={() => handleOpenDialog()} data-testid="add-user-btn">
              <Plus className="mr-2 h-4 w-4" />
              {t('users.addUser')}
            </Button>
          )}
        />

        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <MagnifyingGlass className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t('users.searchUsers')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="search-users-input"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t('reports.user')}</TableHead>
                  <TableHead>{t('users.role')}</TableHead>
                  <TableHead>{t('users.department')}</TableHead>
                  <TableHead>{t('common.status')}</TableHead>
                  <TableHead>{t('common.created')}</TableHead>
                  <TableHead className="text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-zinc-400">
                      <CircleNotch className="w-6 h-6 animate-spin mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-zinc-400">
                      {t('users.noUsers')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id} className="border-zinc-200 hover:bg-zinc-50" data-testid={`user-row-${user.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-zinc-100 flex items-center justify-center">
                            <UserCircle weight="fill" className="w-6 h-6 text-zinc-400" />
                          </div>
                          <div>
                            <p className="text-zinc-900 font-medium">{user.name}</p>
                            <p className="text-sm text-zinc-400">{user.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="px-2 py-1 rounded text-xs font-medium bg-brand-50 text-brand-600 border border-brand-200">
                          {user.role || getRoleName(user.role_id)}
                        </span>
                      </TableCell>
                      <TableCell className="text-zinc-600">{getDeptName(user.department_id)}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-medium border ${
                          user.is_active !== false
                            ? 'bg-green-50 text-green-600 border-green-200'
                            : 'bg-red-50 text-red-600 border-red-200'
                        }`}>
                          {user.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      <TableCell className="text-zinc-500">{formatDate(user.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenDialog(user)}
                            className="text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100"
                            data-testid={`edit-user-${user.id}`}
                          >
                            <PencilSimple className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(user.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-white border-zinc-200 text-zinc-900 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-zinc-900 font-heading">
                {editingUser ? t('users.editUser') : t('users.addUser')}
              </DialogTitle>
              <DialogDescription className="text-zinc-500">
                {editingUser ? t('users.editDesc') : t('users.addDesc')}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label className="text-zinc-700">Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-white border-zinc-300 text-zinc-900"
                  data-testid="user-name-input"
                />
              </div>
              {!editingUser && (
                <>
                  <div className="space-y-2">
                    <Label className="text-zinc-700">Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-white border-zinc-300 text-zinc-900"
                      data-testid="user-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-700">{t('common.password')} *</Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder={t('auth.passwordRequirementsHint', { defaultValue: PASSWORD_POLICY_HINT })}
                        minLength={10}
                        className="bg-white border-zinc-300 text-zinc-900 pr-10"
                        data-testid="user-password-input"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-700"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeSlash className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    <p className="text-xs text-zinc-500">
                      {t('auth.passwordRequirementsHint', { defaultValue: PASSWORD_POLICY_HINT })}
                    </p>
                  </div>
                </>
              )}
              <div className="space-y-2">
                <Label className="text-zinc-700">Role</Label>
                <Select
                  value={formData.role_id}
                  onValueChange={(v) => setFormData({ ...formData, role_id: v })}
                >
                  <SelectTrigger className="bg-white border-zinc-300 text-zinc-900" data-testid="user-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-zinc-200">
                    <SelectItem value="none" className="text-zinc-900">{t('common.noRole')}</SelectItem>
                    {roles.map((role) => (
                      <SelectItem key={role.id} value={role.id} className="text-zinc-900 focus:bg-zinc-100">
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-700">{t('users.department')}</Label>
                <Select
                  value={formData.department_id}
                  onValueChange={(v) => setFormData({ ...formData, department_id: v })}
                >
                  <SelectTrigger className="bg-white border-zinc-300 text-zinc-900" data-testid="user-dept-select">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-zinc-200">
                    <SelectItem value="none" className="text-zinc-900">{t('common.noDepartment')}</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id} className="text-zinc-900 focus:bg-zinc-100">
                        {dept.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="border-zinc-300 text-zinc-700 hover:bg-zinc-100"
                data-testid="cancel-user-btn"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !formData.name || (!editingUser && (!formData.email || !formData.password))}
                className="bg-brand-600 hover:bg-brand-700 text-white"
                data-testid="save-user-btn"
              >
                {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
